import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pencil, User as UserIcon, X } from "lucide-react";
import { AssigneeDisplay } from "./AssigneeDisplay";
import { EditableInputsSection } from "./EditableInputsSection";
import { FieldSectionLabel } from "./FieldSectionLabel";
import { buildAssigneePatch } from "./assigneeSubmit";
import { FieldIssueFrame } from "./FieldIssueFrame";
import { useFieldIssueFrames } from "./stepFieldIssues";
/**
 * Compact side-panel content for a `ProcessDraftAssignment` — the assignee is
 * the only authored field until the assignee themselves records the step.
 */ export function EditableAssignmentContent({ step, onPatch, assigneeLookup, renderSuccessorPicker, allowUnclear, onMarkUnclear, emptyAssigneeLabel, eventTypes, processSteps, fieldIssues }) {
    const { t } = useTranslation();
    const frameFor = useFieldIssueFrames(fieldIssues);
    const [showAssigneeDialog, setShowAssigneeDialog] = useState(false);
    const handleAssigneeSubmit = useCallback((data)=>{
        setShowAssigneeDialog(false);
        if (data.successorStatus === "UNCLEAR") {
            if (onMarkUnclear) void onMarkUnclear();
            return;
        }
        const patch = buildAssigneePatch(data);
        if (patch) void onPatch(patch);
    }, [
        onPatch,
        onMarkUnclear
    ]);
    // The inline X is only meaningful when there's an assignee to clear AND the
    // host has opted into unclear handling. Otherwise the row falls back to the
    // plain edit-on-click affordance.
    const canMarkUnclearInline = Boolean(allowUnclear && onMarkUnclear && step.assignedTo);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-4",
                children: [
                    /*#__PURE__*/ _jsxs(FieldIssueFrame, {
                        ...frameFor("assignedTo"),
                        children: [
                            /*#__PURE__*/ _jsx(FieldSectionLabel, {
                                icon: UserIcon,
                                children: t("processFlow.assignedTo")
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "group flex items-center gap-1",
                                children: [
                                    /*#__PURE__*/ _jsxs("button", {
                                        type: "button",
                                        onClick: ()=>setShowAssigneeDialog(true),
                                        className: "relative flex-1 min-w-0 text-left bg-surface-subtle hover:bg-muted rounded-sm px-2 py-1.5 transition-colors",
                                        children: [
                                            /*#__PURE__*/ _jsx(Pencil, {
                                                className: "absolute top-1.5 right-1.5 w-3 h-3 text-fg-subtle opacity-0 group-hover:opacity-100"
                                            }),
                                            /*#__PURE__*/ _jsx(AssigneeDisplay, {
                                                assignedTo: step.assignedTo,
                                                assignedToModel: step.assignedToModel,
                                                entitySuggestions: step.entitySuggestions,
                                                assigneeLookup: assigneeLookup,
                                                t: t,
                                                emptyLabel: emptyAssigneeLabel
                                            })
                                        ]
                                    }),
                                    canMarkUnclearInline && /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        onClick: ()=>void onMarkUnclear?.(),
                                        title: t("processDraft.contextMenu.markUnclear"),
                                        "aria-label": t("processDraft.contextMenu.markUnclear"),
                                        className: "shrink-0 p-1 rounded-sm text-fg-subtle hover:text-warning hover:bg-warning-surface opacity-0 group-hover:opacity-100 transition-opacity",
                                        children: /*#__PURE__*/ _jsx(X, {
                                            className: "w-4 h-4"
                                        })
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(FieldIssueFrame, {
                        ...frameFor("inputs"),
                        children: /*#__PURE__*/ _jsx(EditableInputsSection, {
                            inputs: step.inputs,
                            currentStepId: step._id,
                            mode: "draft",
                            eventTypes: eventTypes,
                            processSteps: processSteps,
                            onChange: (next)=>void onPatch({
                                    inputs: next
                                })
                        })
                    })
                ]
            }),
            renderSuccessorPicker?.({
                open: showAssigneeDialog,
                onOpenChange: setShowAssigneeDialog,
                value: {
                    persons: [],
                    orgFunctions: [],
                    systems: [],
                    externalContacts: [],
                    successorStatus: ""
                },
                onSave: handleAssigneeSubmit,
                type: "assignee",
                allowUnclear
            })
        ]
    });
}

//# sourceMappingURL=EditableAssignmentContent.js.map